import { useRef, useState } from 'react';
import { FEATURE_CATEGORIES } from './featureCategories';
import { SpotlightBox } from '../primitives/SpotlightBox';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
  ACCENT,
  BORDER,
  EASE,
  FONT_DISPLAY,
  FONT_MONO,
  MUTED,
  SURFACE2,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
} from '../../tokens';

const ACTIVE_TAB_SHADOW = '0 0 12px rgba(255,107,0,0.2), 0 0 28px rgba(255,107,0,0.08)';
const HOVER_TAB_SHADOW = '0 0 22px rgba(255,107,0,0.3), 0 0 50px rgba(255,107,0,0.12)';

/** Zebra background for a feature row. */
const rowBackground = (idx: number): string =>
  idx % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent';

/** Tabbed feature catalogue: a category strip above a detail panel. */
export function FeaturesByCategory() {
  const [active, setActive] = useState(0);
  const isMobile = useIsMobile();
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const activeCategory = FEATURE_CATEGORIES[active] ?? FEATURE_CATEGORIES[0];

  // Selecting a tab scrolls it into the centre of the horizontal strip, so the
  // chosen category is never left half-clipped at an edge.
  const activate = (idx: number): void => {
    setActive(idx);
    requestAnimationFrame(() => {
      tabRefs.current[idx]?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    });
  };

  if (!activeCategory) return null;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          marginLeft: 8,
          overflowX: 'auto',
          overflowY: 'hidden',
          padding: '10px 28px',
          scrollbarWidth: 'none',
        }}
      >
        {FEATURE_CATEGORIES.map((cat, idx) => {
          const isActive = active === idx;

          return (
            <SpotlightBox key={cat.category} style={{ flex: '0 0 auto' }}>
              <button
                type="button"
                ref={(el) => {
                  tabRefs.current[idx] = el;
                }}
                onClick={() => activate(idx)}
                aria-pressed={isActive}
                style={{
                  padding: '14px 18px',
                  borderRadius: 12,
                  border: `1px solid ${isActive ? 'rgba(255,107,0,0.5)' : BORDER}`,
                  background: isActive ? 'rgba(255,107,0,0.1)' : 'rgba(255,255,255,0.02)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  boxShadow: isActive ? ACTIVE_TAB_SHADOW : 'none',
                  transition: `all 0.3s ${EASE}`,
                }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget;
                  el.style.borderColor = 'rgba(255,107,0,0.4)';
                  el.style.background = isActive ? 'rgba(255,107,0,0.1)' : 'rgba(255,107,0,0.05)';
                  el.style.boxShadow = HOVER_TAB_SHADOW;
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget;
                  el.style.borderColor = isActive ? 'rgba(255,107,0,0.5)' : BORDER;
                  el.style.background = isActive ? 'rgba(255,107,0,0.1)' : 'rgba(255,255,255,0.02)';
                  el.style.boxShadow = isActive ? ACTIVE_TAB_SHADOW : 'none';
                }}
              >
                <div style={{ color: isActive ? ACCENT : MUTED, marginBottom: 8, transition: 'color 0.25s' }}>
                  {cat.icon}
                </div>
                <div
                  style={{
                    fontFamily: FONT_MONO,
                    fontSize: '0.68rem',
                    color: isActive ? ACCENT : MUTED,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    transition: 'color 0.25s',
                  }}
                >
                  {cat.category}
                </div>
              </button>
            </SpotlightBox>
          );
        })}
      </div>

      <div
        style={{
          background: SURFACE2,
          border: `1px solid ${BORDER}`,
          borderRadius: 20,
          padding: isMobile ? 24 : 36,
          transition: 'all 0.3s',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <div style={{ color: ACCENT }}>{activeCategory.icon}</div>
          <h3
            style={{
              fontFamily: FONT_DISPLAY,
              fontSize: '1.8rem',
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              color: TEXT_PRIMARY,
            }}
          >
            {activeCategory.category}
          </h3>
        </div>

        <div style={{ width: 48, height: 2, background: ACCENT, borderRadius: 2, marginBottom: 28 }} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {activeCategory.features.map((feature, idx) => (
            <SpotlightBox
              key={feature.name}
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '180px 1fr 1fr',
                gap: isMobile ? 10 : 24,
                padding: '18px 20px',
                background: rowBackground(idx),
                borderRadius: 10,
                borderLeft: `3px solid ${ACCENT}`,
                transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255,107,0,0.04)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = rowBackground(idx);
              }}
            >
              <div>
                <div style={{ fontFamily: FONT_MONO, fontSize: '0.6rem', color: ACCENT, letterSpacing: '0.1em', marginBottom: 4 }}>
                  FEATURE
                </div>
                <div style={{ fontSize: '1rem', fontWeight: 600, color: TEXT_PRIMARY }}>{feature.name}</div>
              </div>

              <div>
                <div style={{ fontFamily: FONT_MONO, fontSize: '0.6rem', color: MUTED, letterSpacing: '0.1em', marginBottom: 4 }}>
                  WHAT IT DOES
                </div>
                <div style={{ fontSize: '0.88rem', color: TEXT_SECONDARY, lineHeight: 1.55 }}>{feature.does}</div>
              </div>

              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: FONT_MONO, fontSize: '0.6rem', color: '#22c55e', letterSpacing: '0.1em', marginBottom: 4 }}>
                  WHY THAT MATTERS
                </div>
                <div style={{ fontSize: '0.88rem', color: TEXT_SECONDARY, lineHeight: 1.55 }}>{feature.benefit}</div>
              </div>
            </SpotlightBox>
          ))}
        </div>
      </div>
    </div>
  );
}
